import { inject, injectable } from 'inversify';
import { BookingKitApiFactoryInterface } from '@/models/Factories/BookingKitApiFactoryInterface';
import { BOOKINGKIT_API_FACTORY, SUPPLIER_STATE_SERVICE } from '@/bootstrap/ServiceProviders';
import { UseSupplierInterface } from '@/state/supplier';
import { InventoryServiceError } from '@/services/InventoryService/Errors/InventoryServiceError';
import {
    fromApiModel as mapExperiencesFromApi,
    fromApiToFlexOffer
} from '@/services/InventoryService/Reducers/ExperienceReducer';
import {
    fromApiModel as mapExperienceAvailabilityFromApi,
} from '@/services/InventoryService/Reducers/ExperienceAvailabilityReducer';
import {
    AddOn,
    Experience,
    FlexOffer,
    Label,
    PaginatedRequest
} from '@/services/InventoryService/types';
import { fromApiModelToLabels } from '@/services/InventoryService/Reducers/LabelsReducer';
import { CreateLabelRequest, UpdateLabelRequest } from '@bookingkit/api-v4/dist/models';
import {fromApiToAddOn} from "@/services/InventoryService/Reducers/AddOnReducer";
import {AddOnListSortByEnum} from "@bookingkit/api-v4";

type GetExperienceAvailabilities = Partial<PaginatedRequest> & {
    experienceId: string,
    startAt: Date,
    endsAt: Date,
    deltaSince?: Date,
    options?: string[],
    requestOptions?: any
}

type PatchAvailabilityRequest = {
    experienceId: string,
    availabilityId: string,
    totalCapacity?: number,
    labels?: string[],
}

@injectable()
export class InventoryService {
    private connection: ReturnType<BookingKitApiFactoryInterface['getConnection']>;

    private supplierId: string;

    constructor(@inject(BOOKINGKIT_API_FACTORY) factory: BookingKitApiFactoryInterface,
                @inject(SUPPLIER_STATE_SERVICE) useSupplierState: () => UseSupplierInterface) {
        const { supplier } = useSupplierState();
        if (!supplier.value?.id) {
            throw InventoryServiceError.fromMissingSupplier();
        }
        this.supplierId = supplier.value.id;
        this.connection = factory.getConnection();
        this.getExperiences = this.getExperiences.bind(this);
    }

    public async patchAvailability(patchAvailabilityInput: PatchAvailabilityRequest) {
        const { supplierId } = this;
        const {
            experienceId, availabilityId, totalCapacity, labels,
        } = patchAvailabilityInput;
        await this.connection
            .patchExperienceAvailability({
                vendorId: supplierId,
                experienceId,
                availabilityId,
                patchAvailabilityRequest: {
                    ...(totalCapacity !== undefined) && { total_capacity: totalCapacity },
                    ...(labels) && { labels },
                }
            });
    }

    public async getExperiences({ active = 1, ...getExperiencesInput }: {active?: 1 | 0} & Partial<PaginatedRequest>): Promise<{ experiences: Experience[], nextPageToken?: string }> {
        const { supplierId } = this;
        const {
            pageSize, nextPageToken, asc,
        } = getExperiencesInput;
        const { data } = await this.connection
            .listExperiences({
                vendorId: supplierId,
                active: active as unknown as boolean,
                pageSize,
                asc: asc as boolean,
                pageToken: nextPageToken
            });
        if (!data) {
            throw InventoryServiceError.fromInvalidProperty('data', 'object', data);
        }
        if (!data?.data) {
            throw InventoryServiceError.fromInvalidProperty('data.data', 'object', data?.data);
        }
        return {
            experiences: data.data.map(mapExperiencesFromApi),
            nextPageToken: data.metadata?.["page-token"],
        };
    }

    public async getFlexOffers({ active = 1, ...getFlexOfferInput }: {active?: 1 | 0} & Partial<PaginatedRequest>): Promise<{ flexOffer: FlexOffer[], nextPageToken?: string }> {
        const { supplierId } = this;
        const {
            pageSize, nextPageToken, asc,
        } = getFlexOfferInput;
        const { data } = await this.connection
            .listFlexOffers({
                vendorId: supplierId,
                pageSize,
                asc: asc as boolean,
                pageToken: nextPageToken
            });
        if (!data) {
            throw InventoryServiceError.fromInvalidProperty('data', 'object', data);
        }
        if (!data?.data) {
            throw InventoryServiceError.fromInvalidProperty('data.data', 'object', data?.data);
        }
        return {
            flexOffer: data.data.map(fromApiToFlexOffer),
            nextPageToken: data.metadata?.["page-token"],
        };
    }

    public async getAddOns(
        getAddOnsInput: Partial<PaginatedRequest> & { sortBy?: AddOnListSortByEnum }
    ): Promise<{ addOns: AddOn[], nextPageToken?: string }> {
        const { supplierId } = this;
        const { pageSize, nextPageToken, asc, sortBy } = getAddOnsInput;
        const { data } = await this.connection
            .addOnList({
                vendorId: supplierId,
                pageSize,
                pageToken: nextPageToken,
                sortBy,
                ...(asc !== undefined) && { asc: (asc ? 1 : 0) as unknown as boolean }
            });

        if (!data) {
            throw InventoryServiceError.fromInvalidProperty('data', 'object', data);
        }

        if (!data?.data) {
            throw InventoryServiceError.fromInvalidProperty('data.data', 'object', data?.data);
        }

        return {
            addOns: data.data.map(fromApiToAddOn),
            nextPageToken: data.metadata?.["page-token"],
        }
    }

    public async getAllExperiences(token?: string): Promise<Experience[]> {
        const { experiences: experiencesFromApi, nextPageToken } = await this.getExperiences({
            active: 1,
            pageSize: 100,
            nextPageToken: token,
        });
        if (!nextPageToken) {
            return experiencesFromApi;
        }

        const nextResults = await this.getAllExperiences(nextPageToken);
        return [...experiencesFromApi, ...nextResults];
    }

    public async getExperienceAvailabilities(getExperienceAvailabilitiesInput: GetExperienceAvailabilities) {
        const { supplierId } = this;
        const {
            experienceId,
            pageSize,
            nextPageToken,
            asc,
            deltaSince,
            endsAt,
            startAt,
            options,
            requestOptions,
        } = getExperienceAvailabilitiesInput;
        const { data } = await this.connection
            .listExperienceAvailabilities({
                vendorId: supplierId,
                experienceId,
                localStartTime: startAt.toISOString(),
                localEndTime: endsAt.toISOString(),
                _options: options ? options.toString() : undefined,
                deltaSince: deltaSince ? deltaSince.toISOString() : undefined,
                pageSize,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                asc: asc ? 1 : 0,
                pageToken: nextPageToken,
                options: requestOptions,
            });

        if (!data.data) {
            throw InventoryServiceError.fromInvalidProperty('data.data', 'object', data?.data);
        }
        return {
            dates: data.data.map(mapExperienceAvailabilityFromApi),
            nextPageToken: data.metadata?.["page-token"],
        };
    }

    public async getLabels(pageSize = 10):Promise<Label[]> {
        const { supplierId } = this;
        const { data } = await this.connection.listLabels({
            vendorId: supplierId,
            pageSize
        });
        if (!data?.data) {
            throw InventoryServiceError.fromInvalidProperty('data.data', 'object', data?.data);
        }
        return data.data.map(fromApiModelToLabels);
    }

    public async createLabel(createLabelRequest: CreateLabelRequest) {
        const { supplierId } = this;
        await this.connection.createLabel({
            vendorId: supplierId,
            createLabelRequest
        });
    }

    public async updateLabel(labelId: string, updateLabelRequest: UpdateLabelRequest) {
        const { supplierId } = this;
        await this.connection.updateLabel({
            vendorId: supplierId,
            labelId,
            updateLabelRequest
        });
    }

    public async deleteLabel(labelId: string) {
        const { supplierId } = this;
        await this.connection.deleteLabel({
            vendorId: supplierId,
            labelId
        });
    }
}

export default { InventoryService };
